'use client';

import React, { useState } from 'react';
import {
  Zap,
  Bot,
  GitBranch,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Clock,
  Send,
  UserCheck,
  ShoppingCart,
  ShieldCheck,
  Smartphone,
  Instagram,
  Globe,
  Tag,
  Database,
  Layers,
  BellRing,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

type BranchKey = 'support' | 'sales' | 'handoff';

interface WorkflowNode {
  id: string;
  type: 'trigger' | 'router' | 'action' | 'output';
  title: string;
  subtitle: string;
  icon: any;
  color: string;
  badge?: string;
  details: string[];
}

export function WorkflowAutomationVisualizer() {
  const [activeBranch, setActiveBranch] = useState<BranchKey>('sales');

  const branches = [
    {
      key: 'sales' as BranchKey,
      title: '1. Automatic Sales & Orders',
      desc: 'Customer asks for pricing or products -> AI shares interactive catalog -> Generates payment link -> Deal Won.',
      color: 'emerald',
      actionNode: {
        title: 'Send Interactive Catalog & Checkout',
        subtitle: 'Product Card + Dynamic Stripe Payment Link',
        badge: 'Instant Purchase',
        details: [
          'Looks up product in CRM catalog',
          'Generates secure 1-click checkout link',
          'Moves deal in pipeline to "Negotiation"',
        ],
      },
      outcomeNode: {
        title: 'Deal Won Automatically',
        subtitle: 'Customer completes payment in WhatsApp or Instagram',
        badge: 'Closed on Autopilot',
        details: [
          'Automatic payment confirmation receipt',
          'Sends customer invoice PDF',
          'Tags contact as #paid_customer',
        ],
      },
    },
    {
      key: 'support' as BranchKey,
      title: '2. 24/7 Support & FAQs',
      desc: 'Customer asks a question -> AI checks your knowledge base -> Replies in under 400ms with zero errors.',
      color: 'purple',
      actionNode: {
        title: 'Search Business Knowledge Base',
        subtitle: 'Retrieves answers from your PDFs, website & FAQs',
        badge: 'Zero Hallucinations',
        details: [
          'Reads your uploaded company documents',
          'Answers in friendly, professional language',
          'Grounded facts with 100% accuracy',
        ],
      },
      outcomeNode: {
        title: 'Customer Inquiry Resolved',
        subtitle: 'Instant answer sent back on WhatsApp or Instagram',
        badge: 'Ticket Closed',
        details: [
          'Replies in under 400 milliseconds',
          'Saves conversation history to CRM',
          'Zero human labor required',
        ],
      },
    },
    {
      key: 'handoff' as BranchKey,
      title: '3. Human Team Takeover',
      desc: 'Customer requests a human or VIP deal -> AI pauses -> Assigns available agent -> Sends phone alert.',
      color: 'blue',
      actionNode: {
        title: 'Assign to Online Team Agent',
        subtitle: 'Routes conversation to the right department automatically',
        badge: 'Instant Alert',
        details: [
          'Checks which agents are online right now',
          'Sends instant mobile push notification',
          'Pauses AI so your agent has full control',
        ],
      },
      outcomeNode: {
        title: 'Agent Chats Live with Full Context',
        subtitle: 'Human agent replies from the web or mobile inbox',
        badge: 'Connected in Seconds',
        details: [
          'Agent sees full chat history and lead notes',
          'AI suggests quick replies to speed up typing',
          'Tags deal as #vip_priority',
        ],
      },
    },
  ];

  const currentBranch = branches.find((b) => b.key === activeBranch)!;

  return (
    <div className="relative mx-auto max-w-6xl w-full text-left">
      {/* Header Info */}
      <div className="text-center mb-10 max-w-3xl mx-auto">
        <Badge
          variant="secondary"
          className="mb-3 px-3.5 py-1 rounded-full text-xs font-semibold border-primary/20 bg-primary/5 text-primary gap-1.5"
        >
          <GitBranch className="size-3.5" />
          Visual Workflow Engine
        </Badge>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
          How It Works: From Message to Closed Deal
        </h2>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
          See how an incoming customer message is understood by AI, answered with your business knowledge, or handed over to your team in seconds.
        </p>

        {/* Branch Selector Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
          {branches.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setActiveBranch(b.key)}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-xs flex items-center gap-2 ${
                activeBranch === b.key
                  ? 'bg-foreground text-background shadow-md scale-105'
                  : 'bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted border border-border/60'
              }`}
            >
              <span
                className={`size-2 rounded-full ${
                  b.color === 'emerald'
                    ? 'bg-emerald-500'
                    : b.color === 'purple'
                    ? 'bg-purple-500'
                    : 'bg-blue-500'
                }`}
              />
              {b.title}
            </button>
          ))}
        </div>
      </div>

      {/* Flow Diagram Canvas */}
      <div className="relative rounded-3xl border border-border/80 bg-card/90 p-6 md:p-10 shadow-2xl backdrop-blur-xl overflow-hidden">
        
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

        {/* Dynamic Glowing Accent Light */}
        <div
          className={`absolute -top-32 -right-32 size-96 rounded-full blur-3xl opacity-20 pointer-events-none transition-all duration-700 ${
            activeBranch === 'sales'
              ? 'bg-emerald-500'
              : activeBranch === 'support'
              ? 'bg-purple-500'
              : 'bg-blue-500'
          }`}
        />

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-8 items-stretch">
          
          {/* STEP 1: TRIGGER NODE */}
          <div className="flex flex-col p-5 rounded-2xl border border-border bg-background/80 shadow-xs relative group hover:border-primary/50 transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="size-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold shadow-xs">
                <Zap className="size-5" />
              </div>
              <Badge variant="outline" className="text-[10px] font-mono bg-muted/50">
                Step 01
              </Badge>
            </div>

            <div className="space-y-1 mb-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Omnichannel Trigger
              </span>
              <h4 className="text-sm font-bold text-foreground">
                Inbound Message Received
              </h4>
              <p className="text-xs text-muted-foreground">
                Customer sends a direct message across any connected instance.
              </p>
            </div>

            {/* Channels Badges */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              <Badge className="bg-emerald-600 text-white text-[10px] font-medium border-none">
                WhatsApp
              </Badge>
              <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-medium border-none flex items-center gap-1">
                <Instagram className="size-2.5" /> Instagram
              </Badge>
              <Badge className="bg-blue-600 text-white text-[10px] font-medium border-none">
                Messenger
              </Badge>
            </div>

            <div className="mt-auto pt-3 border-t border-border/50 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Clock className="size-3 text-emerald-500" />
              <span>Event dispatched in &lt; 20ms</span>
            </div>
          </div>

          {/* STEP 2: NEURAL AI ROUTER */}
          <div className="flex flex-col p-5 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/5 via-primary/10 to-transparent shadow-sm relative group hover:border-primary transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="size-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold shadow-xs">
                <Bot className="size-5" />
              </div>
              <Badge className="text-[10px] font-mono bg-primary text-primary-foreground border-none">
                Step 02 · Core AI
              </Badge>
            </div>

            <div className="space-y-1 mb-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                Zeric Neural Engine
              </span>
              <h4 className="text-sm font-bold text-foreground">
                Intent &amp; Sentiment Analysis
              </h4>
              <p className="text-xs text-muted-foreground">
                Analyzes intent, historical CRM memory, tone, and language automatically.
              </p>
            </div>

            <div className="space-y-1.5 text-[11px] text-muted-foreground bg-background/60 p-2.5 rounded-xl border border-border/60 mb-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">Semantic Intent:</span>
                <span className="font-mono font-bold text-primary capitalize">{activeBranch}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">Confidence:</span>
                <span className="font-mono text-emerald-600 font-bold">99.4%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">Memory:</span>
                <span className="font-mono text-muted-foreground">Session #402</span>
              </div>
            </div>

            <div className="mt-auto pt-3 border-t border-border/50 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Sparkles className="size-3 text-primary" />
              <span>LLM inference in ~300ms</span>
            </div>
          </div>

          {/* STEP 3: DYNAMIC ACTION NODE (Switches by active branch) */}
          <div className="flex flex-col p-5 rounded-2xl border border-border bg-background/80 shadow-xs relative group hover:border-foreground/30 transition-all animate-in fade-in duration-300">
            <div className="flex items-center justify-between mb-4">
              <div
                className={`size-10 rounded-xl flex items-center justify-center font-bold shadow-xs text-white ${
                  activeBranch === 'sales'
                    ? 'bg-emerald-600'
                    : activeBranch === 'support'
                    ? 'bg-purple-600'
                    : 'bg-blue-600'
                }`}
              >
                {activeBranch === 'sales' ? (
                  <ShoppingCart className="size-5" />
                ) : activeBranch === 'support' ? (
                  <Database className="size-5" />
                ) : (
                  <UserCheck className="size-5" />
                )}
              </div>
              <Badge variant="outline" className="text-[10px] font-mono bg-muted/50">
                Step 03
              </Badge>
            </div>

            <div className="space-y-1 mb-3">
              <Badge
                className={`text-[9px] font-semibold border-none ${
                  activeBranch === 'sales'
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : activeBranch === 'support'
                    ? 'bg-purple-500/15 text-purple-600 dark:text-purple-400'
                    : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                }`}
              >
                {currentBranch.actionNode.badge}
              </Badge>
              <h4 className="text-sm font-bold text-foreground">
                {currentBranch.actionNode.title}
              </h4>
              <p className="text-xs text-muted-foreground">
                {currentBranch.actionNode.subtitle}
              </p>
            </div>

            <ul className="space-y-1.5 text-[11px] text-muted-foreground mb-4">
              {currentBranch.actionNode.details.map((detail, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <CheckCircle2 className="size-3 text-primary shrink-0 mt-0.5" />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-3 border-t border-border/50 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Layers className="size-3 text-foreground" />
              <span>Multi-step flow executed</span>
            </div>
          </div>

          {/* STEP 4: FINAL OUTCOME / CLOSING */}
          <div
            className={`flex flex-col p-5 rounded-2xl border shadow-sm relative group transition-all animate-in fade-in duration-300 ${
              activeBranch === 'sales'
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : activeBranch === 'support'
                ? 'border-purple-500/40 bg-purple-500/5'
                : 'border-blue-500/40 bg-blue-500/5'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div
                className={`size-10 rounded-xl flex items-center justify-center font-bold shadow-xs text-white ${
                  activeBranch === 'sales'
                    ? 'bg-emerald-600'
                    : activeBranch === 'support'
                    ? 'bg-purple-600'
                    : 'bg-blue-600'
                }`}
              >
                <CheckCircle2 className="size-5" />
              </div>
              <Badge
                className={`text-[10px] font-mono border-none text-white ${
                  activeBranch === 'sales'
                    ? 'bg-emerald-600'
                    : activeBranch === 'support'
                    ? 'bg-purple-600'
                    : 'bg-blue-600'
                }`}
              >
                Step 04 · Outcome
              </Badge>
            </div>

            <div className="space-y-1 mb-3">
              <span
                className={`text-[10px] font-bold uppercase tracking-wider ${
                  activeBranch === 'sales'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : activeBranch === 'support'
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`}
              >
                {currentBranch.outcomeNode.badge}
              </span>
              <h4 className="text-sm font-bold text-foreground">
                {currentBranch.outcomeNode.title}
              </h4>
              <p className="text-xs text-muted-foreground">
                {currentBranch.outcomeNode.subtitle}
              </p>
            </div>

            <ul className="space-y-1.5 text-[11px] text-muted-foreground mb-4">
              {currentBranch.outcomeNode.details.map((detail, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-3 border-t border-border/50 text-[11px] font-bold text-foreground flex items-center justify-between">
              <span>Status: Completed</span>
              <span className="text-emerald-600 font-mono">100% Automated</span>
            </div>
          </div>

        </div>

        {/* Bottom Banner with Visual Flow Explanation */}
        <div className="mt-8 pt-6 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>
              Currently previewing: <strong className="text-foreground font-semibold">{currentBranch.title}</strong>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline">No code required · Visual Flow Builder ready</span>
            <a
              href="/sign-up"
              className="font-semibold text-primary hover:underline inline-flex items-center gap-1"
            >
              Build Your First Flow <ArrowRight className="size-3" />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
